/**
 * Canonical helpers for inspecting errors that originate from SpacetimeDB
 * procedure calls.
 *
 * Why this needs its own helper: the SpacetimeDB JS SDK rejects procedure
 * promises with the raw `InternalError` string from the wire `ProcedureStatus`
 * variant — see `node_modules/spacetimedb/dist/index.mjs` (`reject(result.value)`
 * with a `t.string()` payload). That means callers can't rely on
 * `e instanceof Error && e.message.includes(...)` to recognize server-thrown
 * `SenderError("[TAG] ...")` messages — the `instanceof` check returns false
 * against a bare string and the recognizer silently falls through.
 *
 * `errorMessage` normalizes both rejection shapes (string or Error) into a
 * plain message string. `hasErrorTag` looks for the `[TAG]` convention we use
 * server-side (see e.g. `getLandlordNegotiationForm` in
 * `spacetimedb/src/dealspace/external.ts` and `getShareableBusinessCard` in
 * `spacetimedb/src/sharing/businessCards/external.ts`). Add a new named
 * matcher here when introducing a new tag, so call sites stay grep-able.
 */
/**
 * Host wrappers SpacetimeDB puts in front of a module-thrown message before it
 * reaches the client. A `SenderError` is our own user-facing copy — "That
 * property isn't one of your target properties." — but it arrives narrated as
 * "The module instance encountered a fatal error: <message>", and
 * `ErrorHandler.default` toasts that string verbatim. The prefix is noise to an
 * owner, and at 47 characters it also eats half the 100-char toast budget, so a
 * real message can get truncated away.
 *
 * Anchored at the start and stripped before anything else inspects the message.
 * `hasErrorTag` is unaffected either way — it uses `includes`.
 */
const HOST_ERROR_PREFIXES = [/^The module instance encountered a fatal error:\s*/i];

function stripHostPrefix(message: string): string {
  for (const prefix of HOST_ERROR_PREFIXES) {
    const stripped = message.replace(prefix, "");
    if (stripped !== message) return stripped.trim();
  }
  return message;
}

export function errorMessage(e: unknown): string {
  if (typeof e === "string") return stripHostPrefix(e);
  if (e instanceof Error) return stripHostPrefix(e.message);
  return "";
}

export function hasErrorTag(e: unknown, tag: string): boolean {
  return errorMessage(e).includes(`[${tag}]`);
}

export const isAuthError = (e: unknown): boolean => hasErrorTag(e, "NOT_AUTHORIZED");
export const isNotFoundError = (e: unknown): boolean => hasErrorTag(e, "NOT_FOUND");
export const isAlreadyExpressedError = (e: unknown): boolean => hasErrorTag(e, "ALREADY_EXPRESSED");
export const isAlreadyOnPlatformError = (e: unknown): boolean =>
  hasErrorTag(e, "ALREADY_ON_PLATFORM");

/**
 * Extract the shareable card UUID from an `[ALREADY_ON_PLATFORM]` error message.
 * Returns `undefined` if the error doesn't match or the UUID can't be parsed.
 */
export function extractCardIdFromAlreadyOnPlatformError(e: unknown): string | undefined {
  if (!isAlreadyOnPlatformError(e)) return undefined;
  const message = errorMessage(e);
  const match = message.match(/\[ALREADY_ON_PLATFORM\]\s*([a-f0-9-]+)/i);
  return match?.[1];
}
